// WHERE TO LAND AFTER SIGNING IN — the one remembered destination, shared by InCite and Harbor.
//
// A signed-out person who opens a deep link (/matrix on an iPad, a link from an email) is shown the
// login screen; the path is stashed so they land where they were going, not on the home page
// (2026-08-27). Two ways that went wrong, both fixed here (2026-10-17):
//
//   1. SIGNING OUT WAS READ AS A DEEP LINK. Signing out re-renders the login screen at the page you
//      were on, and the login screen stashed that page. The NEXT person to sign in on that browser
//      was sent there: on staging the operator signed out on Platform Admin and a program supervisor
//      who signed in next landed on "restricted to platform operators". Signing out now clears the
//      stash and marks the very next login screen as "came from a sign-out", which stashes nothing.
//
//   2. ONE STASH, TWO APPS. InCite and Harbor are separate pages (index.html, harbor.html) with one
//      sign-in. A Harbor path consumed by InCite's router (or the reverse) opened a route that app
//      does not have. A destination is honoured only by the app it belongs to.
//
// Everything is in sessionStorage: per tab, survives the SSO redirect round trip, gone with the tab.

export const AFTER_SIGN_IN_KEY = 'incite.afterSignIn'
export const SIGNED_OUT_KEY = 'incite.signedOut'
export const HARBOR_PREFIX = '/harbor'

const isHarborPath = (p) => p === HARBOR_PREFIX || p.startsWith(`${HARBOR_PREFIX}/`)

/** True when `path` is a page of the app whose home is `home` ('/' = InCite, '/harbor' = Harbor). */
export function belongsToApp(path, home = '/') {
  const p = String(path || '').split(/[?#]/)[0]
  return isHarborPath(home) ? isHarborPath(p) : !isHarborPath(p)
}

/** Only ever an in-app path: an absolute URL would be an open redirect. */
const inAppPath = (to) => typeof to === 'string' && to.startsWith('/') && !to.startsWith('//')

/**
 * Called when the login screen appears. Remembers `here` as the destination unless this login screen
 * is the one a sign-out just produced, or `here` is the root or the email-action page.
 */
export function stashAfterSignIn(here) {
  try {
    if (sessionStorage.getItem(SIGNED_OUT_KEY)) { sessionStorage.removeItem(SIGNED_OUT_KEY); return false }
    if (here && here !== '/' && !here.startsWith('/auth/') && inAppPath(here)) {
      sessionStorage.setItem(AFTER_SIGN_IN_KEY, here)
      return true
    }
  } catch { /* storage unavailable: no deep link, the home page instead */ }
  return false
}

/**
 * Where a successful sign-in lands: the stashed destination if it is an in-app path of THIS app,
 * otherwise `home`. Consumed exactly once — left set, it would redirect the next sign-in on this
 * browser to a stale destination.
 */
export function consumeAfterSignIn(home = '/') {
  try {
    const to = sessionStorage.getItem(AFTER_SIGN_IN_KEY)
    sessionStorage.removeItem(AFTER_SIGN_IN_KEY)
    return inAppPath(to) && belongsToApp(to, home) ? to : home
  } catch { return home }
}

/** Called just before signing out: forget any destination, and mark the next login screen. */
export function markSignedOut() {
  try {
    sessionStorage.removeItem(AFTER_SIGN_IN_KEY)
    sessionStorage.setItem(SIGNED_OUT_KEY, '1')
  } catch { /* storage unavailable: nothing was stashed either */ }
}
